/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Utility functions for dashboard data processing and visualization.
Includes calculations for metrics, chart data formatting, and statistical analysis.
*/

import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subYears, isValid, format, subMonths } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

// CO2 conversion factor: 0.16272 kg CO2 per km (based on UK DEFRA)
export const CO2_CONVERSION_FACTOR = 0.16272;

// Define CO2ByType interface for the CO2 emissions by ride type
export interface CO2ByType {
  rideType: string;
  totalCO2: number;
  totalDistance: number;
}

export type TimePeriod = 'day' | 'week' | 'month' | 'year' | 'all';

export interface CO2Comparison {
  currentCO2: number;
  previousCO2: number;
  currentPeriodLabel: string;
  previousPeriodLabel: string;
}

// Helper function to validate date
export const validateDate = (date: Date | string | null | undefined): Date => {
  if (!date) return new Date();
  
  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  
  if (!isValid(parsedDate) || parsedDate.getFullYear() <= 1970) {
    return new Date(); // Return current date if invalid
  }
  
  return parsedDate;
};

export const getTimeRange = (period: TimePeriod, date: Date = new Date()): { start: Date; end: Date; } => {
  const validDate = validateDate(date);
  
  switch (period) {
    case 'day':
      return {
        start: startOfDay(validDate),
        end: endOfDay(validDate)
      };
    case 'week':
      return {
        start: startOfWeek(validDate, { weekStartsOn: 1 }), // Start on Monday
        end: endOfWeek(validDate, { weekStartsOn: 1 })
      };
    case 'month':
      return {
        start: startOfMonth(validDate),
        end: endOfMonth(validDate)
      };
    case 'year':
      return {
        start: startOfYear(validDate),
        end: endOfYear(validDate)
      };
    case 'all':
      return {
        start: new Date(2000, 0, 1), // Arbitrary past date
        end: new Date(2100, 0, 1) // Arbitrary future date
      };
  }
};

export const getPreviousTimeRange = (period: TimePeriod, date: Date = new Date()): { start: Date; end: Date; } => {
  const validDate = validateDate(date);
  const { start: currentStart, end: currentEnd } = getTimeRange(period, validDate);
  const durationMs = currentEnd.getTime() - currentStart.getTime();
  
  const previousStart = new Date(currentStart.getTime() - durationMs);
  const previousEnd = new Date(currentEnd.getTime() - durationMs);
  
  return { start: previousStart, end: previousEnd };
};

export const getTimeRangeLabel = (period: TimePeriod, date: Date = new Date()): string => {
  const validDate = validateDate(date);
  
  switch (period) {
    case 'day':
      return validDate.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    case 'week':
      const weekStart = startOfWeek(validDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(validDate, { weekStartsOn: 1 });
      return `${weekStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    case 'month':
      return validDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    case 'year':
      return validDate.getFullYear().toString();
    case 'all':
      return 'All Time';
  }
};

// Generate activity data based on the selected time period
export const generateMonthlyActivityData = async (period: TimePeriod = 'month'): Promise<any[]> => {
  try {
    const timeRange = getTimeRange(period);
    
    console.log(`Fetching activity data for period: ${period} from ${format(timeRange.start, 'yyyy-MM-dd')} to ${format(timeRange.end, 'yyyy-MM-dd')}`);
    
    // Get ride data for the specified time range
    const { data: ridesData, error } = await supabase
      .from('rides')
      .select('distance_traveled_km, actual_start_time, created_at')
      .gte('created_at', timeRange.start.toISOString())
      .lte('created_at', timeRange.end.toISOString())
      .order('created_at', { ascending: true });
      
    if (error) {
      console.error("Error fetching rides for activity stats:", error);
      return [];
    }
    
    // Generate time buckets based on period
    const buckets = generateTimeBuckets(period, timeRange);
    
    // Group rides by time bucket
    ridesData.forEach(ride => {
      const rideDate = new Date(ride.actual_start_time || ride.created_at);
      const bucket = findTimeBucket(rideDate, buckets, period);
      
      if (bucket) {
        bucket.rides += 1;
        bucket.distance += Number(ride.distance_traveled_km) || 0;
      }
    });
    
    // Round the distance values
    return buckets.map(bucket => ({
      ...bucket,
      distance: Math.round(bucket.distance * 100) / 100
    }));
  } catch (error) {
    console.error("Error generating activity data:", error);
    return [];
  }
};

// Helper function to generate time buckets based on period
const generateTimeBuckets = (period: TimePeriod, timeRange: { start: Date; end: Date }) => {
  const buckets = [];
  const now = new Date();
  
  switch (period) {
    case 'day': {
      // Show hourly data for the day
      for (let hour = 0; hour < 24; hour++) {
        const hourDate = new Date(timeRange.start);
        hourDate.setHours(hour, 0, 0, 0);
        buckets.push({
          date: hourDate,
          key: format(hourDate, 'HH:mm'),
          month: format(hourDate, 'HH:mm'),
          rides: 0,
          distance: 0
        });
      }
      break;
    }
      
    case 'week': {
      // Show daily data for the week
      const weekStart = new Date(timeRange.start);
      for (let day = 0; day < 7; day++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + day);
        buckets.push({
          date: dayDate,
          key: format(dayDate, 'EEE'),
          month: format(dayDate, 'EEE'),
          rides: 0,
          distance: 0
        });
      }
      break;
    }
      
    case 'month': {
      // Show daily data for the month
      const monthStart = new Date(timeRange.start);
      const monthEnd = new Date(timeRange.end);
      const currentDate = new Date(monthStart);
      
      while (currentDate <= monthEnd) {
        buckets.push({
          date: new Date(currentDate),
          key: format(currentDate, 'dd'),
          month: format(currentDate, 'dd'),
          rides: 0,
          distance: 0
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      break;
    }
      
    case 'year': {
      // Show monthly data for the year
      for (let month = 0; month < 12; month++) {
        const monthDate = new Date(timeRange.start.getFullYear(), month, 1);
        buckets.push({
          date: monthDate,
          key: format(monthDate, 'MMM'),
          month: format(monthDate, 'MMM'),
          rides: 0,
          distance: 0
        });
      }
      break;
    }
      
    case 'all': {
      // Show last 6 months for 'all' view
      for (let i = 0; i < 6; i++) {
        const monthDate = subMonths(now, 5 - i);
        buckets.push({
          date: monthDate,
          key: format(monthDate, 'MMM-yy'),
          month: format(monthDate, 'MMM-yy'),
          rides: 0,
          distance: 0
        });
      }
      break;
    }
  }
  
  return buckets;
};

// Helper function to find the appropriate time bucket for a ride
const findTimeBucket = (rideDate: Date, buckets: any[], period: TimePeriod) => {
  switch (period) {
    case 'day': {
      const rideHour = format(rideDate, 'HH:mm');
      return buckets.find(b => b.key === rideHour.substring(0, 2) + ':00');
    }
      
    case 'week': {
      const rideDay = format(rideDate, 'EEE');
      return buckets.find(b => b.key === rideDay);
    }
      
    case 'month': {
      const rideDay2 = format(rideDate, 'dd');
      return buckets.find(b => b.key === rideDay2);
    }
      
    case 'year': {
      const rideMonth = format(rideDate, 'MMM');
      return buckets.find(b => b.key === rideMonth);
    }
      
    case 'all': {
      const rideMonthYear = format(rideDate, 'MMM-yy');
      return buckets.find(b => b.key === rideMonthYear);
    }
      
    default:
      return buckets[0];
  }
};

export const getCO2ByBookingType = async (period: TimePeriod): Promise<CO2ByType[]> => {
  try {
    console.log(`Fetching CO2 data for period: ${period}`);
    const timeRange = getTimeRange(period);
    
    // Query rides and join with bookings to get ride_type
    const { data, error } = await supabase
      .from('rides')
      .select(`
        distance_traveled_km,
        count_towards_offset,
        booking_id,
        bookings(ride_type)
      `)
      .gte('created_at', timeRange.start.toISOString())
      .lte('created_at', timeRange.end.toISOString());
    
    if (error) {
      console.error("Error fetching CO2 data:", error);
      return [];
    }
    
    // Group by ride type and calculate CO2 savings
    const co2ByType = new Map<string, { totalDistance: number, totalCO2: number }>();
    
    data.forEach(ride => {
      // Safely access ride type from the bookings join
      const rideType = ride.bookings?.ride_type || 'unknown';
      
      // Only count rides marked for offset (or if undefined, count them)
      if (ride.count_towards_offset !== false) {
        const distance = ride.distance_traveled_km || 0;
        const co2Saved = distance * CO2_CONVERSION_FACTOR;
        
        if (!co2ByType.has(rideType)) {
          co2ByType.set(rideType, {
            totalDistance: 0,
            totalCO2: 0
          });
        }
        
        const typeData = co2ByType.get(rideType)!;
        typeData.totalDistance += distance;
        typeData.totalCO2 += co2Saved;
        co2ByType.set(rideType, typeData);
      }
    });
    
    // Convert to array and format for display
    return Array.from(co2ByType.entries()).map(([type, data]) => ({
      rideType: capitalizeFirstLetter(type),
      totalDistance: Math.round(data.totalDistance * 100) / 100,
      totalCO2: Math.round(data.totalCO2 * 100) / 100
    }));
  } catch (error) {
    console.error("Error processing CO2 data:", error);
    return [];
  }
};

export const getCO2Comparison = async (period: TimePeriod): Promise<CO2Comparison> => {
  try {
    // Get current time range
    const currentRange = getTimeRange(period);
    const prevRange = getPreviousTimeRange(period);
    
    // Get labels for display
    const currentRangeLabel = getTimeRangeLabel(period);
    const prevDate = new Date((prevRange.start.getTime() + prevRange.end.getTime()) / 2);
    const prevRangeLabel = getTimeRangeLabel(period, prevDate);
    
    // Fetch current period data
    const { data: currentData, error: currentError } = await supabase
      .from('rides')
      .select('distance_traveled_km, count_towards_offset')
      .gte('created_at', currentRange.start.toISOString())
      .lte('created_at', currentRange.end.toISOString());
    
    if (currentError) {
      console.error("Error fetching current period CO2 data:", currentError);
      return {
        currentCO2: 0,
        previousCO2: 0,
        currentPeriodLabel: currentRangeLabel,
        previousPeriodLabel: prevRangeLabel
      };
    }
    
    // Fetch previous period data
    const { data: prevData, error: prevError } = await supabase
      .from('rides')
      .select('distance_traveled_km, count_towards_offset')
      .gte('created_at', prevRange.start.toISOString())
      .lte('created_at', prevRange.end.toISOString());
    
    if (prevError) {
      console.error("Error fetching previous period CO2 data:", prevError);
      return {
        currentCO2: calculateTotalCO2(currentData || []),
        previousCO2: 0,
        currentPeriodLabel: currentRangeLabel,
        previousPeriodLabel: prevRangeLabel
      };
    }
    
    // Calculate CO2 for each period
    const currentCO2 = calculateTotalCO2(currentData || []);
    const previousCO2 = calculateTotalCO2(prevData || []);
    
    return {
      currentCO2,
      previousCO2,
      currentPeriodLabel: currentRangeLabel,
      previousPeriodLabel: prevRangeLabel
    };
  } catch (error) {
    console.error("Error processing CO2 comparison data:", error);
    return {
      currentCO2: 0,
      previousCO2: 0,
      currentPeriodLabel: getTimeRangeLabel(period),
      previousPeriodLabel: "Previous " + period
    };
  }
};

// Helper function to calculate total CO2 from a set of rides
const calculateTotalCO2 = (rides: any[]): number => {
  let totalCO2 = 0;
  
  rides.forEach(ride => {
    // Only count rides marked for offset (or if undefined, count them)
    if (ride.count_towards_offset !== false) {
      const distance = ride.distance_traveled_km || 0;
      totalCO2 += distance * CO2_CONVERSION_FACTOR;
    }
  });
  
  return Math.round(totalCO2 * 100) / 100;
};

// Helper function to capitalize first letter
const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
